/**
 * Every module that differs between the two revisions, one row each.
 *
 * Unchanged modules are hidden by default: on a revalidation most of the
 * inventory carries forward untouched, and a list of them buries the handful
 * that moved.
 *
 * @module components/portfolio/comparison/ModuleChangeList
 */

import React, { useState } from "react";

import type {
  AcademicChange,
  ModuleDifference,
} from "@/components/portfolio/comparison/diff-types";
import { MODULE_STATUS_DETAILS } from "@/components/portfolio/comparison/module-status";
import { ModuleChangeDetail } from "@/components/portfolio/comparison/ModuleChangeDetail";
import { recordedCreditsLabel } from "@/components/shared/credits";
import {
  Accordion,
  AccordionItem,
  Badge,
  Checkbox,
  EmptyState,
  Heading,
  MutedText,
} from "@/components/ui";

export interface ModuleChangeListProps {
  modules: readonly ModuleDifference[];
  /** The academic changes, keyed `path:kind`, that describe each module's fields. */
  projectedChanges: ReadonlyMap<string, AcademicChange>;
}

/**
 * @example
 * ```tsx
 * <ModuleChangeList modules={differences} projectedChanges={byPathAndKind} />
 * ```
 */
export function ModuleChangeList({
  modules,
  projectedChanges,
}: ModuleChangeListProps): React.ReactElement {
  const [showUnchanged, setShowUnchanged] = useState(false);
  const visible = showUnchanged
    ? modules
    : modules.filter((module) => !module.statuses.includes("unchanged"));

  return (
    <section aria-labelledby="module-changes-heading">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Heading level={2} id="module-changes-heading">
            Module changes
          </Heading>
          <MutedText className="mt-1">
            Edited content and changed curriculum placement are shown separately.
          </MutedText>
        </div>
        <Checkbox
          size="sm"
          checked={showUnchanged}
          onChange={setShowUnchanged}
          label="Show unchanged modules"
        />
      </div>
      {visible.length === 0 ? (
        <EmptyState
          className="mt-6"
          icon="books"
          title="No module changes between these revisions"
          description={
            modules.length > 0
              ? "Show unchanged modules to see the modules carried forward."
              : undefined
          }
          density="row"
          data-testid="comparison-modules-empty"
        />
      ) : (
        <Accordion id="comparison-modules" className="mt-5 space-y-3">
          {visible.map((module) => (
            <AccordionItem
              key={module.id}
              eventKey={`comparison-module-${module.id}`}
              headingLevel={3}
              title={module.title}
              subtitle={`${module.code} · ${recordedCreditsLabel(module.credits)}`}
              headerActions={module.statuses.map((status) => (
                <Badge key={status} size="sm" tone={MODULE_STATUS_DETAILS[status].tone}>
                  {MODULE_STATUS_DETAILS[status].label}
                </Badge>
              ))}
              data-testid={`comparison-module-${module.id}`}
            >
              <ModuleChangeDetail module={module} projectedChanges={projectedChanges} />
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </section>
  );
}
